import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import PhotoViewer from "./Gallery";
import { useTranslation } from "react-i18next";
import { Image } from "lucide-react";

import theme from "../theme";

import { Bath, Tv, Wind, Wifi, Camera, Droplets } from "lucide-react";

const RoomCard = ({
  name,
  image,
  features = [],
  additionalLabel,
  imgs = [],
}) => {
  const { t } = useTranslation();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const handleOpenGallery = (e) => {
    e.stopPropagation();
    setIsGalleryOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
    document.body.style.overflow = "auto";
  };

  const baseAmenities = [
    { icon: Bath, label: t("amenityPrivateBathroom") },
    { icon: Tv, label: t("amenitySmartTV") },
    { icon: Wind, label: t("amenityAirCon") },
    { icon: Wifi, label: t("amenityFastWifi") },
    { icon: Droplets, label: t("amenityBathroomStuff"), colSpan: 2 }
  ];

  return (
    <>
      <div
        className="relative bg-white rounded-sm border-2 overflow-hidden"
        style={{
          backgroundColor: theme.cream,
          borderColor: theme.wheat,
          boxShadow: `10px 10px 0px ${theme.warmBrown}77`,
          filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.05))",
        }}
      >
        {/* Image Section - Increased height */}
        {image ? (
          <div className="relative h-72 overflow-hidden">
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover"
            />

            {/* See pictures button */}
            <div className="absolute top-3 right-3 z-20">
              <button
                onClick={handleOpenGallery}
                className="flex items-center px-3 py-1.5 rounded-full mb-4 ml-3 bg-white cursor-pointer"
              >
                <Image size={18} className="mr-2" />
                {t("btnSeePhotos")}
              </button>
            </div>

            {/* Bottom gradient and title */}
            <div className="absolute inset-0 z-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
              <h3 className="text-2xl font-bold text-white">{name}</h3>
            </div>
          </div>
        ) : (
          <div
            className="relative h-72 flex items-center justify-center"
            style={{ backgroundColor: theme.blush }}
          >
            <div className="text-center">
              <Camera
                size={40}
                style={{ color: theme.warmBrown }}
                className="mx-auto mb-2 opacity-60"
              />
              <span className="text-sm" style={{ color: theme.warmBrown }}>
                {t("photoComingSoon")}
              </span>
            </div>
          </div>
        )}

        {/* Compact Content Section */}
        <div className="p-3">
          {/* Features - More compact layout */}
          {features.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                {features.map(({ icon: Icon, label }, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full text-sm"
                    style={{ backgroundColor: theme.sage + "15" }}
                  >
                    <Icon size={14} style={{ color: theme.sage }} />
                    <span
                      className="text-sm font-medium"
                      style={{ color: theme.warmBrown }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Included amenities - More compact */}
          <div className="mb-3">
            <h4
              className="text-sm font-semibold mb-2"
              style={{ color: theme.warmBrown }}
            >
              {t("includedInRoomHeading")}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {baseAmenities.map((amenity, idx) => {
                const IncludedIcon = amenity.icon;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-1.5 p-1.5 rounded transition-colors ${amenity.colSpan === 2 ? 'col-span-2' : ''}`}
                    style={{
                      backgroundColor: theme.warmBrown + "08",
                    }}
                  >
                    <IncludedIcon
                      size={16}
                      style={{ color: theme.sage + "c0" }}
                    />
                    <span
                      className="text-xs font-medium leading-tight"
                      style={{ color: theme.warmBrown }}
                    >
                      {amenity.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Additional label - More compact */}
          {additionalLabel && (
            <div
              className="px-2 py-1.5 rounded text-center border border-dashed"
              style={{
                borderColor: theme.sage + "40",
                backgroundColor: theme.sage + "08",
                color: theme.sage,
              }}
            >
              <p className="text-sm font-semibold">{additionalLabel}</p>
            </div>
          )}
        </div>
      </div>

      {/* Portal: Render PhotoViewer outside component hierarchy */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isGalleryOpen && (
              <PhotoViewer
                imgs={imgs}
                onClose={handleCloseGallery}
                initialIndex={0}
                className="z-[999999]"
              />
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default RoomCard;
